import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import TaskDashboard from '../Tasks/TaskDashboard'
import TaskCompletionReport from '../Tasks/TaskCompletionReport';
import TaskCalendar from '../Tasks/TaskCalendar';
import CreateTask from '../Tasks/CreateTask';
import { useAuth } from '../Auth/Auth';
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  PlusCircleIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import MoonLoader from 'react-spinners/MoonLoader';

const pages = [
  { name: 'Mechanic Supervisor', to: '/supervisor', component: Link, current: true },
];

const Supervisor = () => {
  const [activeComponent, setActiveComponent] = useState('dashboard');
  const { role, loading } = useAuth();

  const renderComponent = () => {
    switch (activeComponent) {
      case 'dashboard':
        return <TaskDashboard />;
      case 'completionReport':
        return <TaskCompletionReport />;
      case 'calendar':
        return <TaskCalendar />;
      case 'createTask':
        return <CreateTask />;
      default:
        return <TaskDashboard />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
        <div className="text-center">
          <MoonLoader size={60} color="#F97316" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin' && role !== 'Supervisor') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
              <WrenchScrewdriverIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Mechanic Administration</h1>
              <p className="text-sm text-gray-600 mt-1">Manage tasks, reports, and mechanic schedules</p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveComponent('dashboard')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'dashboard'
                    ? 'bg-white text-orange-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <ClipboardDocumentListIcon className="w-4 h-4 inline-block mr-2" />
                Task Dashboard
              </button>

              <button
                onClick={() => setActiveComponent('completionReport')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'completionReport'
                    ? 'bg-white text-orange-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <ChartBarIcon className="w-4 h-4 inline-block mr-2" />
                Completion Report
              </button>

              <button
                onClick={() => setActiveComponent('calendar')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'calendar'
                    ? 'bg-white text-orange-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <CalendarDaysIcon className="w-4 h-4 inline-block mr-2" />
                Task Calendar
              </button>

              <button
                onClick={() => setActiveComponent('createTask')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'createTask'
                    ? 'bg-white text-orange-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <PlusCircleIcon className="w-4 h-4 inline-block mr-2" />
                Create Task
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {renderComponent()}
        </div>
      </div>
    </div>
  );
};

export default Supervisor;
